import { RECURRING_INTERVALS } from "components/FormerEditor/common/constants";
import { formattedPriceToUnitAmount } from "components/FormerEditor/common/currency";
import {
  TEMPLATE_CAMPAIGN_AUDIENCE_INTENT,
  TEMPLATE_CAMPAIGN_BUILDER_SECTIONS,
  ALL_TEMPLATE_PAGE_BUILDER_SECTIONS,
  TEMPLATE_PAGE_TYPE_BUILDER_SECTIONS,
  TEMPLATE_PRODUCT_PICKER_TABS,
  TEMPLATE_ACTION
} from "utils/constants/template";
import { getAllTemplateEntities, entitiesForTemplate } from "utils/entity";
import {
  TEMPLATE_CAMPAIGN_BUILDER,
  TEMPLATE_LINK_BUILDER,
  TEMPLATE_PAGE_BUILDER
} from "./analytics";
import get from "lodash/get";
import compact from "lodash/compact";
import { prepareCreateShortLinkInitialValues } from "./shortLink";
import { STATE_KEYS } from "./constants/state";
import { getCreateCampaignScaffold } from "./campaign";
import { TEMPLATE_PAGE_TYPE } from "components/FormerEditor/common/constants/entity";

/**
 * Track the User selections within the link template builder
 * - gives us an idea of the aggregate selections made by the user
 * @param {Object} values - values selected by the user
 * @param {Object} base - base object for initial spread
 */
export const getTemplateLinkCompleteEventMetadata = ({
  shortLink,
  checkoutPreview,
  tracking
}) => {
  const primaries = get(shortLink, STATE_KEYS.SHORT_LINK.PRIMARIES) || [];
  const prices = get(shortLink, STATE_KEYS.SHORT_LINK.PRICES) || [];
  const models = compact([...primaries, ...prices]);
  const currency = get(models, "[0].currency");

  const formattedTotal = get(checkoutPreview, "checkout.banner.label");
  const total = formattedPriceToUnitAmount({
    formattedPrice: formattedTotal,
    currency
  });

  return {
    ...tracking,
    currency,
    products: models.length,
    total
  };
};

/**
 * Track the User selections within the page template builder
 * - gives us an idea of the aggregate selections made by the user
 * @param {Object} values - values selected by the user
 * @param {Object} base - base object for initial spread
 */
export const getTemplatePageCompleteEventMetadata = (values, base) => {
  const rollup = values.primaries.reduce((memo, primary) => {
    const { interval, currency } = primary;
    const productMatch = values.products.find(
      ({ id }) => id === primary.product
    );
    if (productMatch) {
      const priceMatch = productMatch.prices.find(
        ({ id }) => id === primary.price
      );
      if (priceMatch) {
        if (!memo[currency]) {
          memo[currency] = {};
        }
        if (typeof memo[currency][interval] === "undefined") {
          memo[currency][interval] = 0;
        }
        memo[currency][interval] += priceMatch.unit_amount;
      }
    }

    return memo;
  }, {});
  const currencies = Object.keys(rollup);
  const reducedSubtotals = currencies.reduce((memo, currency) => {
    const subtotals = rollup[currency];
    for (const interval in subtotals) {
      const subtotal = subtotals[interval];
      memo[`${currency}_${interval}_subtotal`] = subtotal;
      memo[`${currency}_${interval}_average`] = Math.floor(
        subtotal / values.products.length
      );
    }
    return memo;
  }, {});

  return {
    ...base,
    blocks: values.entities.length,
    block_types: values.entities.map(({ uid }) => uid),
    products: values.products.length,
    features: values.features ? values.features.length : 0,
    feature_groups: values.featureGroups ? values.featureGroups.length : 0,
    currencies,
    ...reducedSubtotals
  };
};

export const getTemplatePageInitialValues = ({ appEntities, tracking }) => {
  const allEntities = getAllTemplateEntities(appEntities);
  const template = TEMPLATE_PAGE_TYPE.FORM;
  const templateEntities = entitiesForTemplate(template, allEntities);

  return {
    form: {
      action: TEMPLATE_ACTION.NEW,
      context: TEMPLATE_PAGE_BUILDER,
      copyManifestUUID: null,
      // Override here to skip to sections during dev
      activeSection: ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.BLOCKS,
      /**
       * !WARNING!
       * - Is the entities setup the same as the starting point
       */
      template,
      entities: allEntities,
      sections: TEMPLATE_PAGE_TYPE_BUILDER_SECTIONS[template],
      tracking,
      products: {
        tabs: [
          TEMPLATE_PRODUCT_PICKER_TABS.TEMPLATE,
          TEMPLATE_PRODUCT_PICKER_TABS.NEW,
          TEMPLATE_PRODUCT_PICKER_TABS.PRODUCT
        ]
      },
      prices: {
        intervals: [RECURRING_INTERVALS.YEAR, RECURRING_INTERVALS.MONTH]
      }
    },
    entities: templateEntities,
    products: [],
    primaries: [],
    // Initializer based on flex product
    prices: []
  };
};

export const getTemplateLinkInitialValues = ({
  tracking,
  application,
  merchantAccount
}) => {
  return {
    form: {
      context: TEMPLATE_LINK_BUILDER,
      // Override here to skip to sections during dev
      activeSection: ALL_TEMPLATE_PAGE_BUILDER_SECTIONS.LINKS,
      tracking
    },
    ...prepareCreateShortLinkInitialValues({
      application,
      merchantAccount
    })
  };
};

export const getTemplateCampaignInitialValues = ({
  tracking,
  application,
  merchantAccount
}) => {
  return {
    form: {
      context: TEMPLATE_CAMPAIGN_BUILDER,
      sections: TEMPLATE_CAMPAIGN_BUILDER_SECTIONS,
      // Override here to skip to sections during dev
      activeSection: TEMPLATE_CAMPAIGN_BUILDER_SECTIONS.DETAILS,
      audience: {
        intent: TEMPLATE_CAMPAIGN_AUDIENCE_INTENT.AUDIENCE
      },
      tracking
    },
    ...getCreateCampaignScaffold({ application, merchantAccount })
  };
};
